const REMOTE_SCHEME_RE = /^(https?:)?\/\//i;
const NON_HTTP_SCHEME_RE = /^(data|blob):/i;

export function toSafePublicPath(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (REMOTE_SCHEME_RE.test(trimmed) || NON_HTTP_SCHEME_RE.test(trimmed)) {
    return trimmed;
  }
  const publicBase = (process.env.PUBLIC_URL ?? "").replace(/\/+$/, "");
  if (trimmed.startsWith("/")) {
    return `${publicBase}${trimmed}`;
  }
  return `${publicBase}/${trimmed.replace(/^\/+/, "")}`;
}

export function buildImageCandidateUrls(
  primary?: string | null,
  fallbacks: Array<string | null | undefined> = []
): string[] {
  const seen = new Set<string>();
  const candidates = [primary, ...fallbacks]
    .map((value) => (value ? toSafePublicPath(value) : ""))
    .filter(Boolean)
    .filter((url) => {
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
  return candidates;
}

interface PreloadOptions {
  concurrency?: number;
  signal?: AbortSignal;
}

function preloadSingle(url: string, signal?: AbortSignal): Promise<void> {
  if (typeof Image === "undefined") return Promise.resolve();
  if (signal?.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

export async function preloadImageUrls(
  urls: string[],
  options: PreloadOptions = {}
): Promise<void> {
  if (typeof window === "undefined" || urls.length === 0) return;
  const { signal } = options;
  const concurrency = Math.max(1, Math.min(8, options.concurrency ?? 4));
  const queue = Array.from(new Set(urls.filter(Boolean)));
  let cursor = 0;

  const workers = Array.from({ length: Math.min(concurrency, queue.length) }).map(
    async () => {
      while (cursor < queue.length) {
        if (signal?.aborted) return;
        const currentIndex = cursor;
        cursor += 1;
        const url = queue[currentIndex];
        await preloadSingle(url, signal);
        if (signal?.aborted) return;
      }
    }
  );
  await Promise.all(workers);
}
